import { createSlice } from '@reduxjs/toolkit';

import type { ModelLead } from '@app/api/Leads/types';

import type { PayloadAction } from '@reduxjs/toolkit';

export interface LeadsLatestRegistryResultState {
  leads: ModelLead[];
  totalCount: number | null;
  searchId: string | null;
  searchSignature: string | null;
  maxSize: number;
  requestedMaxSize: number;
  isLoading: boolean;
  isNextPageLoading: boolean;
  hasAcceptedResult: boolean;
  isStale: boolean;
  refreshRequestId: number;
}

interface SetLatestRegistryResultPayload {
  leads: ModelLead[];
  totalCount: number | null;
  searchId: string | null;
  searchSignature: string;
  maxSize: number;
  requestedMaxSize: number;
}

export const LEADS_DEFAULT_MAX_SIZE = 20;

const initialState: LeadsLatestRegistryResultState = {
  leads: [],
  totalCount: null,
  searchId: null,
  searchSignature: null,
  maxSize: LEADS_DEFAULT_MAX_SIZE,
  requestedMaxSize: LEADS_DEFAULT_MAX_SIZE,
  isLoading: false,
  isNextPageLoading: false,
  hasAcceptedResult: false,
  isStale: false,
  refreshRequestId: 0,
};

const latestRegistryResultSlice = createSlice({
  name: 'leadsLatestRegistryResult',
  initialState,
  reducers: {
    setLatestRegistryResult: (
      state,
      action: PayloadAction<SetLatestRegistryResultPayload>
    ) => {
      state.leads = action.payload.leads;
      state.totalCount = action.payload.totalCount;
      state.searchId = action.payload.searchId;
      state.searchSignature = action.payload.searchSignature;
      state.maxSize = action.payload.maxSize;
      state.requestedMaxSize = action.payload.requestedMaxSize;
      state.isLoading = false;
      state.isNextPageLoading = false;
      state.hasAcceptedResult = true;
      state.isStale = false;
    },
    setLatestRegistryResultLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setLatestRegistryNextPageLoading: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isNextPageLoading = action.payload;
    },
    requestLatestRegistryNextPage: (state) => {
      state.requestedMaxSize += state.maxSize;
    },
    // Explicitly refresh the currently mounted registry without changing its
    // filters or loaded window. The request id is an effect trigger; `isStale`
    // also preserves the refresh requirement if the registry remounts first.
    requestLatestRegistryRefresh: (state) => {
      state.refreshRequestId += 1;
      state.isStale = true;
    },
    // Realtime patch: replace only the list rows (e.g. `onLeadsGroupUpdate`)
    // without touching the accepted searchId/totalCount/pagination.
    patchLatestRegistryLeads: (state, action: PayloadAction<ModelLead[]>) => {
      if (!state.hasAcceptedResult) return;

      state.leads = action.payload;
    },
    // Targeted single-lead patch (e.g. optimistic responsible/funnel-stage
    // updates from a row). Replaces only the matching lead so per-row cells can
    // update one row without subscribing to (and re-rendering on) the whole
    // leads array.
    replaceLatestRegistryLead: (state, action: PayloadAction<ModelLead>) => {
      if (!state.hasAcceptedResult) return;

      const index = state.leads.findIndex(
        (lead) => lead.Id === action.payload.Id
      );

      if (index === -1) return;

      state.leads[index] = action.payload;
    },
    // Mark the accepted result as out of date after an external change that the
    // registry cannot observe in place (e.g. a task created/completed from the
    // Lead Card). The registry keeps showing the current rows but refetches on
    // its next mount instead of reusing the cached result via the remount-skip.
    markLatestRegistryResultStale: (state) => {
      if (!state.hasAcceptedResult) return;

      state.isStale = true;
    },
    resetLatestRegistryResult: () => initialState,
  },
});

const { actions, reducer } = latestRegistryResultSlice;

export const {
  markLatestRegistryResultStale,
  patchLatestRegistryLeads,
  replaceLatestRegistryLead,
  requestLatestRegistryNextPage,
  requestLatestRegistryRefresh,
  resetLatestRegistryResult,
  setLatestRegistryNextPageLoading,
  setLatestRegistryResult,
  setLatestRegistryResultLoading,
} = actions;

export const leadsLatestRegistryResultReducer = reducer;
